/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { UserClaims } from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUser: BehaviorSubject<UserClaims | null> = new BehaviorSubject<UserClaims | null>(null);
  private isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private oktaAuthStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) {
    this.initializeAuth();
  }

  private async initializeAuth() {
    try {
      // First check the current authentication state
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      this.isAuthenticated.next(isAuthenticated);

      if (isAuthenticated) {
        const user = await this.oktaAuth.getUser();
        this.currentUser.next(user);
        this.router.navigate(['/codes']);
      }

      // Subscribe to auth state changes
      this.oktaAuthStateService.authState$.subscribe({
        next: async (authState) => {
          const newAuthState = authState.isAuthenticated ?? false;
          this.isAuthenticated.next(newAuthState);
          if (newAuthState) {
            try {
              const user = await this.oktaAuth.getUser();
              this.currentUser.next(user);
              // Add explicit navigation after successful authentication
              this.router.navigate(['/codes']);
            } catch (error) {
              console.error('Error fetching user info:', error);
              this.currentUser.next(null);
            }
          } else {
            const isAuthenticated = await this.oktaAuth.isAuthenticated();
            this.isAuthenticated.next(isAuthenticated);
            if (isAuthenticated) {
              const user = await this.oktaAuth.getUser();
              this.currentUser.next(user);
            }
          }
        },
        error: (error) => {
          console.error('Error in auth state subscription:', error);
          this.isAuthenticated.next(false);
          this.currentUser.next(null);
        }
      });
    } catch (error) {
      console.error('Error initializing auth:', error);
      this.isAuthenticated.next(false);
      this.currentUser.next(null);
      this.router.navigateByUrl('/login', { replaceUrl: true });
    }
  }

  getCurrentUser(): Observable<UserClaims | null> {
    return this.currentUser.asObservable();
  }

  getIsAuthenticated(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  getCurrentUserId(): string | undefined {
    return this.currentUser.value?.sub;
  }

  isAdmin(): boolean {
    const user = this.currentUser.value;
    if (user) {
      const groups = user['groups'] as string[] || [];
      return groups.includes('admin');
    }
    return false;
  }

  async signOut() {
    try {
      await this.oktaAuth.signOut();
      this.currentUser.next(null);
      this.isAuthenticated.next(false);
      this.router.navigateByUrl('/login', { replaceUrl: true });
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  }

  async login() {
    try {
      await this.oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error('Error during login:', error);
    }
  }
}